import React, { useState } from 'react';
import { Link } from 'react-router-dom';
// Removed 'Link' to avoid Router crashes if not using react-router-dom at root
import { ArrowRight, Loader2, X, ScrollText, CheckCircle2, AlertTriangle, ExternalLink, Users, Heart, Search, Sparkles, ChevronRight, ChevronLeft, Globe } from 'lucide-react';
import { FamilyMember, RelationType } from '../types';
import { COUNTRY_CODES } from '../constants';
import { auth, db } from '../services/firebaseConfig';
import { getInitialUserRole, logAdminAssignment } from '../services/adminHelper';
import { signInWithPopup, GoogleAuthProvider, User } from 'firebase/auth';
import { doc, updateDoc } from 'firebase/firestore';

interface LoginScreenProps {
  members: Record<string, FamilyMember>;
  onLogin: (userId: string) => void;
  onRegister: (newMember: FamilyMember) => Promise<void>; // Updated to Promise for async handling
  onClaim: (memberId: string) => void;
}

const PlainBackground = () => (
  <div className="absolute inset-0 z-0 bg-slate-950">
    <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-950 to-black"></div>
  </div>
);

// --- FEATURES TOUR COMPONENT ---
const FeaturesModal: React.FC<{ isOpen: boolean; onClose: () => void }> = ({ isOpen, onClose }) => {
  const [slide, setSlide] = useState(0);
  
  const features = [

    {

      title: "Interactive Tree",

      desc: "Navigate generations seamlessly with our fluid, zoomable family tree interface. Visualize complex relationships instantly.",

      icon: Users,

      color: "bg-indigo-500"

    },

    {

      title: "Private Social Feed",

      desc: "A dedicated space for family updates. Share photos, milestones, and memories securely within your kinship circle.",

      icon: Heart,

      color: "bg-rose-500"

    },

    {

      title: "Smart Directory",

      desc: "Find relatives by profession, location, or blood group. Discover connections you never knew existed.",

      icon: Search,

      color: "bg-emerald-500"

    },
    
  ];

  if (!isOpen) return null;

  const nextSlide = () => setSlide(prev => (prev + 1) % features.length);
  const prevSlide = () => setSlide(prev => (prev - 1 + features.length) % features.length);
  const CurrentIcon = features[slide].icon;

  return (
    <div className="fixed inset-0 bg-black/80 z-[100] flex items-center justify-center p-6 backdrop-blur-md animate-fade-in">
      <div className="bg-white rounded-[2.5rem] w-full max-w-md shadow-2xl overflow-hidden relative">
        <button onClick={onClose} className="absolute top-6 right-6 p-2 bg-gray-50 hover:bg-gray-100 rounded-full z-10 transition-colors"><X className="w-5 h-5 text-gray-500" /></button>
        <div className="p-8 pb-0 pt-12 flex flex-col items-center text-center">
          <div className={`w-20 h-20 rounded-3xl ${features[slide].color} flex items-center justify-center mb-6 shadow-xl`}><CurrentIcon className="w-10 h-10 text-white" /></div>
          <h3 className="text-2xl font-black text-gray-900 mb-3">{features[slide].title}</h3>
          <p className="text-sm text-gray-500 font-medium leading-relaxed max-w-xs">{features[slide].desc}</p>
        </div>
        <div className="p-8 flex items-center justify-between mt-4">
          <button onClick={prevSlide} className="p-3 rounded-xl hover:bg-gray-50 text-gray-400 hover:text-gray-900 transition-colors"><ChevronLeft className="w-6 h-6" /></button>
          <div className="flex gap-2">{features.map((_, i) => <div key={i} className={`w-2 h-2 rounded-full transition-all ${i === slide ? 'bg-gray-900 w-4' : 'bg-gray-300'}`} />)}</div>
          <button onClick={nextSlide} className="p-3 rounded-xl hover:bg-gray-50 text-gray-400 hover:text-gray-900 transition-colors"><ChevronRight className="w-6 h-6" /></button>
        </div>
        <div className="bg-gray-50 p-4 text-center"><button onClick={onClose} className="text-xs font-black uppercase text-indigo-600 tracking-widest hover:underline">Start Using App</button></div>
      </div>
    </div>
  );
};

const TermsModal: React.FC<{ isOpen: boolean; onClose: () => void }> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-4 backdrop-blur-md animate-fade-in">
      <div className="bg-white rounded-[2.5rem] w-full max-w-lg shadow-2xl flex flex-col max-h-[80vh] overflow-hidden">
        <div className="p-6 border-b border-gray-100 flex items-center justify-between"><div className="flex items-center gap-3"><ScrollText className="w-5 h-5 text-indigo-600" /><h3 className="text-xl font-black text-gray-900">Terms of Use</h3></div><button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors"><X className="w-5 h-5 text-gray-400" /></button></div>
        <div className="flex-1 overflow-y-auto p-8 space-y-6 text-sm text-gray-600 leading-relaxed font-medium">
          <section>

            <h4 className="text-gray-900 font-bold mb-2">1. Trial Version Notice</h4>

            <p>Famitry is currently provided as a "Trial Version" (Beta). The service is provided on an "as-is" and "as-available" basis for evaluation purposes. No guarantees are made regarding uptime, data retention, or feature completeness.</p>

          </section>

          <section>

            <h4 className="text-gray-900 font-bold mb-2">2. User Responsibility</h4>

            <p>You agree to provide accurate and lawful information when documenting your family heritage. You are solely responsible for the content you submit and its compliance with applicable laws.</p>

          </section>

          <section>

            <h4 className="text-gray-900 font-bold mb-2">3. Data Privacy & Security</h4>

            <p>We take reasonable measures to protect your data, but cannot guarantee absolute security. Your data may be stored on third-party servers (e.g., Google Firebase). By using Famitry, you consent to such storage and processing. Please review our Privacy Policy for details.</p>

          </section>

          <section>

            <h4 className="text-gray-900 font-bold mb-2">4. Data Retention & Deletion</h4>

            <p>You may request deletion of your data at any time. We reserve the right to retain or remove data as required by law or for operational purposes.</p>

          </section>

          <section>

            <h4 className="text-gray-900 font-bold mb-2">5. Third-Party Services</h4>

            <p>Famitry integrates with third-party services (such as Google, Firebase, and others). We are not responsible for the practices or content of these services. Your use of such services is subject to their respective terms and privacy policies.</p>

          </section>

          <section>

            <h4 className="text-gray-900 font-bold mb-2">6. Limitation of Liability</h4>

            <p>Famitry and its developers are not liable for any direct, indirect, incidental, or consequential damages arising from your use of the app. Use at your own risk.</p>

          </section>

          <section>

            <h4 className="text-gray-900 font-bold mb-2">7. Indemnification</h4>

            <p>You agree to indemnify and hold harmless Famitry, its developers, and affiliates from any claims, damages, or expenses arising from your use of the app or violation of these terms.</p>

          </section>

          <section>

            <h4 className="text-gray-900 font-bold mb-2">8. Dispute Resolution</h4>

            <p>Any disputes arising from your use of Famitry will be governed by the laws of your jurisdiction. We encourage users to resolve disputes amicably.</p>

          </section>

          <section>

            <h4 className="text-gray-900 font-bold mb-2">9. Changes to Terms</h4>

            <p>We may update these Terms of Use at any time. Continued use of Famitry constitutes acceptance of the latest terms.</p>

          </section>

          <section>

            <h4 className="text-gray-900 font-bold mb-2">10. Contact</h4>

            <p>For questions or concerns, contact us at <a href="mailto:admin@famitry.in" className="text-indigo-600 underline">admin@famitry.in</a>.</p>

          </section>
        </div>
        <div className="p-6 bg-gray-50 flex justify-end"><button onClick={onClose} className="px-8 py-3 bg-indigo-600 text-white rounded-xl font-black text-xs uppercase tracking-widest shadow-lg hover:bg-indigo-700 transition-all">I Understand</button></div>
      </div>
    </div>
  );
};

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" xmlns="http://www.w3.org/2000/svg">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

const LoginScreen: React.FC<LoginScreenProps> = ({ members, onLogin, onRegister, onClaim }) => {
  const [step, setStep] = useState<'PHONE' | 'VERIFY' | 'REGISTER'>('PHONE');
  const [countryCode, setCountryCode] = useState('+91');
  const [phone, setPhone] = useState('');
  const [hasAgreed, setHasAgreed] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showFeatures, setShowFeatures] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const [authError, setAuthError] = useState<{ title: string; message: string; isConfigError?: boolean } | null>(null);
  const [firebaseUser, setFirebaseUser] = useState<User | null>(null);
  
  // NEW: Track inviteCode/familyId from URL params
  const [invitedFamilyId, setInvitedFamilyId] = useState<string | null>(null);
  
  const [regData, setRegData] = useState({ name: '', gender: 'Male' as 'Male' | 'Female' | 'Other', dob: '' });

  // NEW: Read URL parameters on mount
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const familyId = urlParams.get('familyId');
    if (familyId) {
      setInvitedFamilyId(familyId);
      console.log('[INVITE] Detected familyId in URL:', familyId);
    }
  }, []);

  const fullPhoneNumber = `${countryCode}${phone.trim()}`;

  const handlePhoneSubmit = () => {
    if (phone.length < 5) {
      alert("Please enter a valid phone number.");
      return;
    }
    setStep('VERIFY');
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    setAuthError(null);
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      setFirebaseUser(user);

      const memberList = Object.values(members);
      // Logic: Does this phone number already exist in our DB?

      // In a real app, you'd query Firestore. Here we search the prop.

      const existingMember = memberList.find(m => m.phoneNumber === fullPhoneNumber);

      if (existingMember) {
        // --- CLAIM PROFILE LOGIC ---
        // 1. Security Check
        if (existingMember.email && existingMember.email !== user.email) {
            throw new Error("This number is already linked to another email.");
        }
        
        // 2. Update Member Record (Link Email)
        // Note: Using updated collection structure ("members" instead of "family-data")
        const memberRef = doc(db, "members", existingMember.id);
        
        // NEW: If user was invited and profile doesn't have familyId, assign the invited one
        const updateData: any = {
            email: user.email,
            joined: true,
            avatarUrl: existingMember.avatarUrl || user.photoURL,
            phoneNumber: fullPhoneNumber // Normalize DB entry
        };
        
        // If invited and existing member doesn't have familyId, assign it
        if (invitedFamilyId && (!existingMember.familyId || existingMember.familyId === 'unknown')) {
            updateData.familyId = invitedFamilyId;
            console.log('[CLAIM] Assigning invited familyId to existing member:', invitedFamilyId);
        }
        
        await updateDoc(memberRef, updateData);
        
        onClaim(existingMember.id);
      } else {
        // NEW: If invited, show register screen with pre-filled familyId context
        console.log('[NO PROFILE] User needs to register. Invited familyId:', invitedFamilyId);
        setStep('REGISTER');
      }
    } catch (error: any) {
      console.error("Auth Error:", error);
      let msg = "Authentication failed.";
      let isConfig = false;
      if (error.code === 'auth/unauthorized-domain') {
        msg = `Domain not allowed. Add '${window.location.hostname}' in Firebase Console > Auth > Settings.`;
        isConfig = true;
      }
      setAuthError({ title: "Sign-In Error", message: error.message || msg, isConfigError: isConfig });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="min-h-screen flex flex-col items-center justify-center relative overflow-hidden font-sans select-none">
        <PlainBackground />

        <div className="w-full max-w-md z-10 p-6 flex flex-col h-full md:h-auto justify-center">
          <div className="mb-10 text-center animate-slide-down">
            <h1 className="text-5xl font-black text-white tracking-tighter mb-2 drop-shadow-lg">Famitry</h1>
            <p className="text-slate-400 text-lg font-medium tracking-wide">Preserve your legacy.</p>
            <div className="mt-6 p-4 bg-white/5 rounded-2xl border border-white/10 backdrop-blur-sm">
                <p className="text-gray-300 text-xs mb-3 leading-relaxed">Connect with your Family roots and heritage.</p>
                <button onClick={() => setShowFeatures(true)} className="px-4 py-2 bg-yellow-400 hover:bg-yellow-300 text-black rounded-lg text-xs font-black uppercase tracking-widest transition-all flex items-center gap-2 mx-auto shadow-lg shadow-yellow-400/20">
                    <Sparkles className="w-3 h-3" /> Explore Features
                </button>
            </div>
          </div>

          <div className="bg-white/95 backdrop-blur-2xl p-6 md:p-8 rounded-[2rem] shadow-2xl border border-white/20">
            {step === 'PHONE' && (
              <div className="space-y-6">
                <div className="space-y-4">
                  <label className="text-xs font-black text-gray-400 uppercase tracking-widest">Mobile Number</label>
                  <div className="flex gap-2">
                    <select value={countryCode} onChange={(e) => setCountryCode(e.target.value)} className="w-28 md:w-32 bg-gray-100 border border-gray-200 rounded-xl px-2 py-4 text-xs font-bold text-gray-900 focus:ring-2 focus:ring-indigo-500 outline-none text-center truncate">
                      {COUNTRY_CODES.map((c, i) => <option key={`${c.code}-${c.country}-${i}`} value={c.code}>{c.code} ({c.country})</option>)}
                    </select>
                    <input type="tel" placeholder="Number" className="flex-1 min-w-0 px-4 py-4 bg-gray-100 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none font-bold text-gray-900 text-sm md:text-base" value={phone} onChange={(e) => setPhone(e.target.value)} />
                  </div>
                  <div className="flex items-start gap-3 p-1">
                    <button onClick={() => setHasAgreed(!hasAgreed)} className={`mt-0.5 shrink-0 w-5 h-5 rounded border-2 transition-all flex items-center justify-center ${hasAgreed ? 'bg-indigo-600 border-indigo-600 text-white' : 'bg-gray-100 border-gray-200'}`}>{hasAgreed && <CheckCircle2 className="w-4 h-4" />}</button>
                    <p className="text-[11px] text-gray-500 font-bold leading-snug">
                       I agree to the <button onClick={() => setShowTerms(true)} className="text-indigo-600 hover:underline">Terms of Use</button>.
                    </p>
                  </div>
                  <button onClick={handlePhoneSubmit} disabled={loading || phone.length < 5 || !hasAgreed} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-4 rounded-xl font-black transition-all flex items-center justify-center gap-2 disabled:opacity-50 shadow-lg">Continue <ArrowRight className="w-4 h-4" /></button>
                </div>
              </div>
            )}

            {step === 'VERIFY' && (
              <div className="animate-fade-in space-y-6">
                <button onClick={() => { setStep('PHONE'); setAuthError(null); }} className="text-xs font-bold text-gray-400 hover:text-indigo-600 flex items-center gap-1 mb-2"><ArrowRight className="w-3 h-3 rotate-180" /> Back</button>
                <div className="text-center mb-6"><h3 className="text-xl font-bold text-gray-900">Sign in to Validate</h3><p className="text-xs text-gray-500 mt-1">One-time authentication for {fullPhoneNumber}</p></div>
                {authError && (
                  <div className={`mb-6 p-4 rounded-xl text-sm flex gap-3 items-start ${authError.isConfigError ? 'bg-amber-50 text-amber-800 border border-amber-200' : 'bg-rose-50 text-rose-800 border border-rose-200'}`}>
                    <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
                    <div className="text-left w-full"><h4 className="font-bold mb-1">{authError.title}</h4><p className="opacity-90 leading-relaxed text-xs mb-2">{authError.message}</p></div>
                  </div>
                )}
                <button onClick={handleGoogleLogin} disabled={loading} className="w-full flex items-center justify-center gap-3 bg-white border border-gray-200 py-4 rounded-xl font-bold text-gray-700 hover:bg-gray-50 transition-all shadow-sm group">
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><GoogleIcon /><span>Continue with Google</span></>}
                </button>
                <p className="text-[10px] text-center text-gray-400 mt-4 leading-tight">We use Google Sign-In to securely verify your identity. <br/> We link this account to your mobile number.</p>
              </div>
            )}

            {step === 'REGISTER' && (
              <div className="animate-fade-in space-y-4">
                <h3 className="text-lg font-bold text-gray-900 text-center">Complete Your Profile</h3>
                <div className="space-y-4">
                  <input type="text" className="w-full px-4 py-3 bg-gray-100 border border-gray-200 rounded-xl font-bold text-gray-900 placeholder:text-gray-400" placeholder="Full Name" value={regData.name} onChange={(e) => setRegData({...regData, name: e.target.value})} />
                  <select className="w-full px-4 py-3 bg-gray-100 border border-gray-200 rounded-xl font-bold text-gray-900" value={regData.gender} onChange={(e) => setRegData({...regData, gender: e.target.value as any})}><option value="Male">Male</option><option value="Female">Female</option><option value="Female">Others</option></select>
                  <div className="relative"><label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-1.5 ml-1">Date of Birth</label><input type="date" className="w-full px-4 py-3 bg-gray-100 border border-gray-200 rounded-xl font-bold text-gray-900" onChange={(e) => setRegData({...regData, dob: e.target.value})} placeholder="DD/MM/YYYY" /></div>
                  
                  <button 
                    onClick={async () => {
                      if (!regData.name) return;
                      setLoading(true);
                      const currentUser = firebaseUser || auth.currentUser;
                      const userRole = getInitialUserRole(fullPhoneNumber);
                      logAdminAssignment(fullPhoneNumber, userRole);
                      
                      try {
                        // NEW: Use invitedFamilyId if available, otherwise create a new one
                        const familyId = invitedFamilyId || currentUser?.uid || `family-${Date.now()}`;
                        console.log('[REGISTER] Using familyId:', familyId, 'From invite:', invitedFamilyId);
                        
                        await onRegister({
                          id: currentUser?.uid || `user-${Date.now()}`,
                          familyId: familyId,  // CRITICAL: Every member must have a familyId (either from invite or new)
                          name: regData.name,
                          gender: regData.gender,
                          dob: regData.dob || '1990-01-01',
                          relation: RelationType.SELF,
                          phoneNumber: fullPhoneNumber,
                          email: currentUser?.email || undefined,
                          joined: true,
                          role: userRole,
                          avatarUrl: currentUser?.photoURL || `https://picsum.photos/seed/${Date.now()}/200/200`,
                          maritalStatus: 'Single',
                          isMarried: false,
                          parentIds: [],
                          childrenIds: [],
                        });
                      } catch (e) {
                        console.error(e);
                        setAuthError({ title: "Registration Failed", message: "Could not create profile. Try again." });
                      } finally {
                        setLoading(false);
                      }
                    }}
                    disabled={loading || !regData.name}
                    className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-4 rounded-xl font-black shadow-lg mt-2 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Start My Journey"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="fixed bottom-4 left-0 right-0 flex justify-center text-center text-xs text-gray-400 px-4">
           {/* Replaced Link with span or button to avoid crash if no router */}
           <p>By continuing, you agree to our <Link to="/terms" className="text-indigo-600 hover:underline">Terms of Use</Link> and <Link to="/privacy" className="text-indigo-600 hover:underline">Privacy Policy</Link>.</p>
        </div>
      </div>
      <TermsModal isOpen={showTerms} onClose={() => setShowTerms(false)} />
      <FeaturesModal isOpen={showFeatures} onClose={() => setShowFeatures(false)} />
    </>
  );
};

export default LoginScreen;